import React, { useState, useRef, useEffect, useMemo } from "react";
import styles from "./index.module.css";

export interface TypeaheadOption {
  label: string;
  value: string;
  sublabel?: string;
  group?: string;
}

export interface TypeaheadProps {
  value: string;
  options: TypeaheadOption[];
  onSelect: (option: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
}

const matchesQuery = (option: TypeaheadOption, query: string) => {
  const q = query.toLowerCase();
  return (
    option.label.toLowerCase().includes(q) ||
    (option.sublabel?.toLowerCase().includes(q) ?? false)
  );
};

export function Typeahead({
  value,
  options,
  onSelect,
  placeholder = "Search...",
  style = {},
  inputStyle = {},
}: TypeaheadProps) {
  const selectedOption = options.find((option) => option.value === value);
  const [query, setQuery] = useState(selectedOption?.label ?? value ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);

  const containerRef = useRef<HTMLDivElement>(null);

  // Sorted by group then label so options belonging to the same dataset sit
  // together — headers are inserted at render time based on this order.
  const filteredOptions = useMemo(() => {
    return options
      .filter((option) => matchesQuery(option, query))
      .sort((a, b) => {
        const groupCompare = (a.group || "").localeCompare(b.group || "");
        return groupCompare !== 0 ? groupCompare : a.label.localeCompare(b.label);
      });
  }, [options, query]);

  useEffect(() => {
    const matched = options.find((option) => option.value === value);
    setQuery(matched?.label ?? value ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Only show the read-only dataset tag while the field reflects the
  // committed selection — hide it as soon as the user starts editing/
  // searching so it never looks like part of the editable text.
  const showSelectedTag =
    !isOpen && !!selectedOption?.sublabel && query === selectedOption.label;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        setHighlightIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0,
        );
        break;

      case "ArrowUp":
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1,
        );
        break;

      case "Enter":
        if (highlightIndex >= 0) {
          const selected = filteredOptions[highlightIndex];
          onSelect(selected.value);
          setQuery(selected.label);
          setIsOpen(false);
        }
        break;

      case "Escape":
        setIsOpen(false);
        break;
    }
  };

  return (
    <div ref={containerRef} className={styles.typeahead} style={{ ...style }}>
      <input
        style={showSelectedTag ? { ...inputStyle, paddingRight: 90 } : inputStyle}
        type="text"
        value={query}
        placeholder={placeholder}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
          setHighlightIndex(-1);
        }}
        onKeyDown={handleKeyDown}
        className={styles.input}
      />
      {showSelectedTag && (
        <span className={styles.selectedTag}>{selectedOption.sublabel}</span>
      )}

      {isOpen && filteredOptions.length > 0 && (
        <ul className={styles.list}>
          {filteredOptions.map((option, index) => {
            const showGroupHeader =
              !!option.group &&
              (index === 0 || filteredOptions[index - 1].group !== option.group);
            return (
              <React.Fragment key={option.value}>
                {showGroupHeader && (
                  <li className={styles.groupHeader} aria-hidden="true">
                    {option.group}
                  </li>
                )}
                <li
                  onMouseDown={() => {
                    onSelect(option.value);
                    setQuery(option.label);
                    setIsOpen(false);
                  }}
                  className={styles.listItem}
                  style={{
                    background:
                      index === highlightIndex ? "var(--Primary-Color-Shade-1)" : "transparent",
                    color: index === highlightIndex ? "white" : "black",
                  }}
                >
                  <span className={styles.listItemLabel}>{option.label}</span>
                  {option.sublabel && !option.group && (
                    <span
                      className={styles.listItemSublabel}
                      style={{
                        color: index === highlightIndex ? "#dce9f5" : "#6b7280",
                      }}
                    >
                      {option.sublabel}
                    </span>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
}
export default Typeahead;
